import React, { useState, useRef } from 'react';
import { doc, updateDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { compressImage } from '../../utils/api'; 
import LocationSearchInput from '../../components/ui/LocationSearchInput';
import { GEMINI_API_KEY } from '../../config/firebase'; 

export default function AddListingForm({ user, db, storage, onComplete, initialData, isMapsScriptLoaded }) {
    // Basic Info
    const [title, setTitle] = useState(initialData?.title || '');
    const [description, setDescription] = useState(initialData?.description || '');
    const [price, setPrice] = useState(initialData?.price || '');
    const [location, setLocation] = useState(initialData?.location || '');
    const [category, setCategory] = useState(initialData?.category || 'tableware');
    const [idealFor, setIdealFor] = useState(initialData?.idealFor?.join(', ') || '');
    
    // Separated Dimensions
    const [dimLength, setDimLength] = useState(initialData?.dimLength || '');
    const [dimWidth, setDimWidth] = useState(initialData?.dimWidth || '');
    const [dimHeight, setDimHeight] = useState(initialData?.dimHeight || '');
    const [dimUnit, setDimUnit] = useState(initialData?.dimUnit || 'ft');

    // Main Images
    const [newImageFiles, setNewImageFiles] = useState([]);
    const [existingImageUrls, setExistingImageUrls] = useState(initialData?.imageUrls || []);
    const [newImagePreviews, setNewImagePreviews] = useState([]);

    // Options with individual inventory and images
    const [options, setOptions] = useState(initialData?.options || []);
    const [newOption, setNewOption] = useState({ name: '', inventory: 1, price: '', file: null, preview: '' });

    // Additional Details
    const [tags, setTags] = useState(initialData?.tags || []);
    const [currentTag, setCurrentTag] = useState('');
    const [includedItems, setIncludedItems] = useState(initialData?.includedItems || []);
    const [currentItem, setCurrentItem] = useState('');
    const [isNonReturnable, setIsNonReturnable] = useState(initialData?.isNonReturnable || false);
    
    // Venue Specific
    const [capacity, setCapacity] = useState(initialData?.capacity || '');
    const [amenities, setAmenities] = useState(initialData?.amenities || []);
    const [currentAmenity, setCurrentAmenity] = useState('');
    const [houseRules, setHouseRules] = useState(initialData?.houseRules || '');

    // Fulfillment
    const [canPickup, setCanPickup] = useState(initialData?.canPickup !== false);
    const [canDeliver, setCanDeliver] = useState(initialData?.canDeliver || false);
    const [deliveryPricingType, setDeliveryPricingType] = useState(initialData?.deliveryPricingType || 'flat');
    const [deliveryFee, setDeliveryFee] = useState(initialData?.deliveryFee || 0);
    const [freeDeliveryMinQuantity, setFreeDeliveryMinQuantity] = useState(initialData?.freeDeliveryMinQuantity || '');
    const [freeDeliveryMinTotal, setFreeDeliveryMinTotal] = useState(initialData?.freeDeliveryMinTotal || '');
    
    // Buffer/turnover days
    const [bufferDays, setBufferDays] = useState(initialData?.bufferDays || 0);

    // Main Inventory (for items without options)
    const [mainInventory, setMainInventory] = useState(initialData?.inventory || 1);

    // UI State
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);

    const handleMainImageChange = async (e) => {
        if (e.target.files) {
            const filesArray = Array.from(e.target.files);
            const compressedFiles = await Promise.all(filesArray.map(file => compressImage(file, 800, 0.7)));
            
            setNewImageFiles(prev => [...prev, ...compressedFiles]);
            const previews = compressedFiles.map(file => URL.createObjectURL(file));
            setNewImagePreviews(prev => [...prev, ...previews]);
        }
    };
    
    const handleRemoveNewImage = (index) => {
        setNewImageFiles(newImageFiles.filter((_, i) => i !== index));
        setNewImagePreviews(newImagePreviews.filter((_, i) => i !== index));
    };

    const handleRemoveExistingImage = (index) => {
        setExistingImageUrls(existingImageUrls.filter((_, i) => i !== index));
    };

    const handleOptionImageChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const compressed = await compressImage(file, 600, 0.7);
            setNewOption(prev => ({ ...prev, file: compressed, preview: URL.createObjectURL(compressed) }));
        }
    };
    
    const handleAddTag = () => {
        if (currentTag.trim() !== '' && !tags.includes(currentTag.trim().toLowerCase())) {
            setTags([...tags, currentTag.trim().toLowerCase()]);
            setCurrentTag('');
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setTags(tags.filter(tag => tag !== tagToRemove));
    };

    const handleAddItem = () => {
        if (currentItem.trim() !== '') {
            setIncludedItems([...includedItems, currentItem.trim()]);
            setCurrentItem('');
        }
    };
    
    const handleAddAmenity = () => {
        if (currentAmenity.trim() !== '') {
            setAmenities([...amenities, currentAmenity.trim()]);
            setCurrentAmenity('');
        }
    };

    const handleRemoveAmenity = (indexToRemove) => {
        setAmenities(amenities.filter((_, index) => index !== indexToRemove));
    };

    const handleRemoveItem = (indexToRemove) => {
        setIncludedItems(includedItems.filter((_, index) => index !== indexToRemove));
    };

    const handleAddOption = () => {
        if (!newOption.name || newOption.inventory < 0) {
            setError("Please provide a valid option name and inventory count.");
            return;
        }
        setOptions([...options, newOption]);
        setNewOption({ name: '', inventory: 1, price: '', file: null, preview: '' });
        setError('');
    };

    const handleRemoveOption = (indexToRemove) => {
        setOptions(options.filter((_, index) => index !== indexToRemove));
    };

    const handleGenerateDescription = async () => {
        if (!title) {
            setError("Please enter a title to generate a description.");
            return;
        }
        setAiLoading(true);
        const keywords = `${title}, ${category}, ${idealFor}`;
        const prompt = `Write a compelling and professional product description for a party rental item. Use the following keywords as inspiration: "${keywords}". The description should be about 2-3 sentences long.`;
        
        try {
            const payload = { contents: [{ role: "user", parts: [{ text: prompt }] }] };
            // Ensure GEMINI_API_KEY is available in scope or imported
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${window.GEMINI_API_KEY || ''}`;
            const response = await fetch(apiUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            const result = await response.json();
            if (result.candidates?.[0]) {
                setDescription(result.candidates[0].content.parts[0].text);
            } else {
                throw new Error("AI failed to generate a description.");
            }
        } catch (err) {
            setError("Could not generate description. Please try again.");
        } finally {
            setAiLoading(false);
        }
    };

    const handleProofread = async () => {
        if (!description) return;
        setAiLoading(true);
        const prompt = `Proofread and correct any grammar or spelling mistakes in the following text, keeping the original tone and meaning. Only return the corrected text:\n\n"${description}"`;
        try {
            const payload = { contents: [{ role: "user", parts: [{ text: prompt }] }] };
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${window.GEMINI_API_KEY || ''}`;
            const response = await fetch(apiUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            const result = await response.json();
            if (result.candidates?.[0]) {
                setDescription(result.candidates[0].content.parts[0].text);
            } else {
                throw new Error("AI failed to proofread.");
            }
        } catch (err) {
            setError("Could not proofread description. Please try again.");
        } finally {
            setAiLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const prohibitedTerms = ['venmo', 'zelle', 'paypal', 'cashapp', 'pay directly', 'wire transfer', 'pay me via'];
        const contentToCheck = (title + ' ' + description).toLowerCase();
        if (prohibitedTerms.some(term => contentToCheck.includes(term))) {
            setError("Listings cannot mention off-platform payment methods (e.g. Venmo, Zelle). All payments must be processed securely through Host-it!.");
            setLoading(false);
            return;
        }

        if (existingImageUrls.length + newImageFiles.length === 0) {
            setError("Please upload at least one image for your listing.");
            setLoading(false);
            return;
        }

        try {
            const newImageUrls = await Promise.all(
                newImageFiles.map(async (file) => {
                    const filePath = `listings/${user.uid}/${Date.now()}_${file.name}`;
                    const fileRef = ref(storage, filePath);
                    await uploadBytes(fileRef, file);
                    return await getDownloadURL(fileRef);
                })
            );

            const updatedOptions = await Promise.all(
                options.map(async (opt) => {
                    if (opt.file) {
                        const filePath = `listings/${user.uid}/options/${Date.now()}_${opt.file.name}`;
                        const fileRef = ref(storage, filePath);
                        await uploadBytes(fileRef, opt.file);
                        const imageUrl = await getDownloadURL(fileRef);
                        return { name: opt.name, inventory: opt.inventory, price: parseFloat(opt.price || price), imageUrl };
                    }
                    return { name: opt.name, inventory: opt.inventory, price: parseFloat(opt.price || price), imageUrl: opt.imageUrl || '' };
                })
            );

            const totalInventory = options.length > 0
                ? updatedOptions.reduce((acc, opt) => acc + opt.inventory, 0)
                : parseInt(mainInventory);

            let formattedDimensions = '';
            const dimParts = [];
            if (dimLength) dimParts.push(`${dimLength} ${dimUnit} L`);
            if (dimWidth) dimParts.push(`${dimWidth} ${dimUnit} W`);
            if (dimHeight) dimParts.push(`${dimHeight} ${dimUnit} H`);
            if (dimParts.length > 0) formattedDimensions = dimParts.join(' x ');

            const listingData = {
                title, description, price: parseFloat(price), location, category,
                idealFor: idealFor.split(',').map(tag => tag.trim()).filter(Boolean),
                dimensions: formattedDimensions, 
                dimLength, dimWidth, dimHeight, dimUnit, 
                tags, includedItems, isNonReturnable, canPickup, canDeliver, deliveryPricingType, deliveryFee: parseFloat(deliveryFee || 0),
                freeDeliveryMinQuantity: parseInt(freeDeliveryMinQuantity) || null,
                freeDeliveryMinTotal: parseFloat(freeDeliveryMinTotal) || null,
                bufferDays: parseInt(bufferDays) || 0,
                options: updatedOptions,
                inventory: totalInventory,
                imageUrls: [...existingImageUrls, ...newImageUrls],
                vendorId: user.uid,
                capacity: category === 'venues' ? parseInt(capacity) || null : null,
                amenities: category === 'venues' ? amenities : [],
                houseRules: category === 'venues' ? houseRules : '',
            };

            if (initialData) {
                const listingRef = doc(db, "listings", initialData.id);
                await updateDoc(listingRef, listingData);
            } else {
                await addDoc(collection(db, "listings"), { ...listingData, createdAt: serverTimestamp() });
            }
            onComplete();
        } catch (err) {
            console.error("Error saving listing:", err);
            setError("Failed to save listing. " + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg mt-4 border border-pink-200">
            <h3 className="text-xl font-bold mb-4">{initialData ? 'Edit Listing' : 'New Listing Details'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
                <input type="text" placeholder="Listing Title" value={title} onChange={e => setTitle(e.target.value)} className="w-full p-2 border rounded" required />
                <div>
                    <textarea placeholder="Description" value={description} onChange={e => setDescription(e.target.value)} className="w-full p-2 border rounded" rows="4"></textarea>
                    <div className="flex justify-end gap-2 mt-1">
                        <button type="button" onClick={handleGenerateDescription} disabled={aiLoading} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">✨ AI Generate</button>
                        <button type="button" onClick={handleProofread} disabled={aiLoading} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">✔️ AI Proofread</button>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input type="number" placeholder="Price per day (base)" value={price} onChange={e => setPrice(e.target.value)} className="p-2 border rounded" required />
                    <select value={category} onChange={e => setCategory(e.target.value)} className="p-2 border rounded">
                        <option value="tableware">Tableware</option>
                        <option value="venues">Venues</option>
                        <option value="tents-canopies">Tents & Canopies</option>
                        <option value="tables-chairs">Tables & Chairs</option>
                        <option value="decorations">Decorations</option>
                        <option value="djs">DJs</option>
                        <option value="entertainment">Entertainment</option>
                        <option value="food">Food & Catering</option>
                        <option value="mobile-bars">Mobile Bars</option>
                        <option value="florals">Florals</option>
                        <option value="bakery">Bakery & Cakes</option>
                        <option value="photography">Photography</option>
                        <option value="videography">Videography</option>
                        <option value="content-creation">Content Creation</option>
                        <option value="audio-visual">Audio/Visual</option>
                        <option value="staffing">Event Staffing</option>
                        <option value="transportation">Transportation</option>
                        <option value="mobile-restrooms">Mobile Restrooms</option>
                        <option value="packages">Packages</option>
                        <option value="planners">Event Planners</option>
                    </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input type="text" placeholder="Ideal For (e.g. weddings, birthdays)" value={idealFor} onChange={e => setIdealFor(e.target.value)} className="w-full p-2 border rounded" />
                    <div className="flex gap-2 items-center bg-gray-50 border rounded p-1">
                        <input type="number" placeholder="L" value={dimLength} onChange={e => setDimLength(e.target.value)} className="w-full p-1.5 border rounded text-center text-sm focus:ring-1 focus:ring-pink-500 outline-none" title="Length" />
                        <span className="text-gray-400 font-bold text-xs">X</span>
                        <input type="number" placeholder="W" value={dimWidth} onChange={e => setDimWidth(e.target.value)} className="w-full p-1.5 border rounded text-center text-sm focus:ring-1 focus:ring-pink-500 outline-none" title="Width" />
                        <span className="text-gray-400 font-bold text-xs">X</span>
                        <input type="number" placeholder="H" value={dimHeight} onChange={e => setDimHeight(e.target.value)} className="w-full p-1.5 border rounded text-center text-sm focus:ring-1 focus:ring-pink-500 outline-none" title="Height" />
                        <select value={dimUnit} onChange={e => setDimUnit(e.target.value)} className="p-1.5 border rounded bg-white text-sm font-semibold outline-none focus:ring-1 focus:ring-pink-500 cursor-pointer text-gray-700">
                            <option value="ft">ft</option>
                            <option value="in">in</option>
                        </select>
                    </div>
                </div>
                
                {/* Note: This assumes LocationSearchInput is imported globally or passed down */}
                {/* <LocationSearchInput initialValue={location} onPlaceSelected={setLocation} isFormInput={true} isMapsScriptLoaded={isMapsScriptLoaded} /> */}
                <input type="text" placeholder="Location" value={location} onChange={e => setLocation(e.target.value)} className="w-full p-2 border rounded" required />

                <div>
                    <label className="block text-sm font-medium text-gray-700">Main Images</label>
                    <div className="mt-2 flex flex-wrap gap-2 items-center">
                        {existingImageUrls.map((url, index) => (
                            <div key={url} className="relative">
                                <img src={url} alt="existing preview" className="h-24 w-24 object-cover rounded"/>
                                <button type="button" onClick={() => handleRemoveExistingImage(index)} className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 text-xs">×</button>
                            </div>
                        ))}
                        {newImagePreviews.map((preview, index) => (
                            <div key={preview} className="relative">
                                <img src={preview} alt="new preview" className="h-24 w-24 object-cover rounded"/>
                                <button type="button" onClick={() => handleRemoveNewImage(index)} className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 text-xs">×</button>
                            </div>
                        ))}
                        <label htmlFor="image-upload" className="cursor-pointer h-24 w-24 bg-gray-100 rounded flex items-center justify-center border-2 border-dashed hover:border-pink-400 hover:bg-gray-200">
                            <span className="text-4xl text-gray-400">+</span>
                        </label>
                        <input id="image-upload" type="file" multiple onChange={handleMainImageChange} className="hidden"/>
                    </div>
                </div>
                
                <div className="border-t pt-4">
                    <h4 className="font-semibold mb-2">Options & Inventory</h4>
                    <p className="text-xs text-gray-500 mb-2">Add options if your item comes in different sizes, colors, etc. If not, just set the main inventory below.</p>
                    <div className="space-y-2">
                        {options.map((opt, index) => (
                            <div key={index} className="flex items-center gap-2 bg-gray-50 p-2 rounded-md">
                                {opt.preview || opt.imageUrl ? <img src={opt.preview || opt.imageUrl} className="w-10 h-10 rounded object-cover" /> : <div className="w-10 h-10 rounded bg-gray-200" />}
                                <p className="flex-grow font-medium">{opt.name}</p>
                                <p className="text-sm">Inv:</p>
                                <p className="font-semibold">{opt.inventory}</p>
                                <p className="text-sm">Price:</p>
                                <p className="font-semibold">${opt.price || price}</p>
                                <button type="button" onClick={() => handleRemoveOption(index)} className="text-red-500 font-bold">×</button>
                            </div>
                        ))}
                    </div>
                    <div className="flex flex-col sm:flex-row items-end gap-2 border-t pt-4 mt-4">
                        <input type="text" placeholder="Option Name (e.g., Large, Red)" value={newOption.name} onChange={e => setNewOption({...newOption, name: e.target.value})} className="p-2 border rounded-md flex-grow w-full sm:w-auto" />
                        <input type="number" placeholder="Inv." value={newOption.inventory} onChange={e => setNewOption({...newOption, inventory: parseInt(e.target.value) || 0})} className="p-2 border rounded-md w-full sm:w-20" />
                        <input type="number" placeholder="Price (optional)" value={newOption.price} onChange={e => setNewOption({...newOption, price: e.target.value})} className="p-2 border rounded-md w-full sm:w-24" />
                        <div className="w-full sm:w-auto">
                            <label className="text-xs">Image (optional)</label>
                            <input type="file" onChange={handleOptionImageChange} className="text-xs w-full" />
                        </div>
                        <button type="button" onClick={handleAddOption} className="bg-blue-500 text-white px-4 py-2 rounded-md font-semibold w-full sm:w-auto">Add</button>
                    </div>
                    {options.length === 0 && (
                        <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700">Main Inventory</label>
                            <input type="number" placeholder="Max inventory available per day" value={mainInventory} onChange={e => setMainInventory(e.target.value)} className="w-full p-2 border rounded-md" />
                        </div>
                    )}
                </div>
                
                <div className="border-t pt-4 space-y-2">
                    <h4 className="font-semibold">Additional Details</h4>
                    <div className="flex items-center space-x-4">
                        <label><input type="checkbox" checked={canPickup} onChange={(e) => setCanPickup(e.target.checked)} /> Allow Pickup</label>
                        <label><input type="checkbox" checked={canDeliver} onChange={(e) => setCanDeliver(e.target.checked)} /> Allow Delivery</label>
                        <label><input type="checkbox" checked={isNonReturnable} onChange={(e) => setIsNonReturnable(e.target.checked)} /> Non-returnable</label>
                    </div>
                    {canDeliver && (
                        <div className="space-y-2 pt-2">
                            <div className="flex gap-2">
                                <select value={deliveryPricingType} onChange={e => setDeliveryPricingType(e.target.value)} className="p-2 border rounded-md w-1/3 bg-white">
                                    <option value="flat">Flat Fee</option>
                                    <option value="per_mile">Per Mile</option>
                                </select>
                                <input type="number" placeholder={deliveryPricingType === 'per_mile' ? "Fee per Mile ($)" : "Flat Delivery Fee ($)"} value={deliveryFee} onChange={e => setDeliveryFee(e.target.value)} className="p-2 border rounded-md w-2/3" />
                            </div>
                            <div className="flex items-center space-x-2 mt-1">
                                <input type="number" placeholder="Free delivery for qty of..." value={freeDeliveryMinQuantity} onChange={(e) => setFreeDeliveryMinQuantity(e.target.value)} className="w-1/2 p-2 border rounded" />
                                <input type="number" placeholder="...or orders over $" value={freeDeliveryMinTotal} onChange={(e) => setFreeDeliveryMinTotal(e.target.value)} className="w-1/2 p-2 border rounded" />
                            </div>
                        </div>
                    )}
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Turnover / Buffer Days</label>
                        <p className="text-xs text-gray-500 mb-1">Days needed to clean or restock this item after a rental before it can be booked again.</p>
                        <input 
                            type="number" 
                            min="0" 
                            value={bufferDays} 
                            onChange={(e) => setBufferDays(e.target.value)} 
                            className="w-full p-2 border rounded-md" 
                            placeholder="0" 
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                        <div className="flex">
                            <input type="text" value={currentTag} onChange={e => setCurrentTag(e.target.value)} placeholder="e.g., wedding, outdoor" className="flex-grow p-2 border rounded-l-md" />
                            <button type="button" onClick={handleAddTag} className="bg-blue-500 text-white px-4 py-2 rounded-r-md">Add Tag</button>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {tags.map((tag, index) => (
                                <div key={index} className="flex items-center bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                                    {tag}
                                    <button type="button" onClick={() => handleRemoveTag(tag)} className="ml-2 text-blue-500 hover:text-blue-700">×</button>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">What's Included</label>
                        <div className="flex">
                            <input type="text" value={currentItem} onChange={e => setCurrentItem(e.target.value)} placeholder="e.g., 10 chairs" className="flex-grow p-2 border rounded-l-md" />
                            <button type="button" onClick={handleAddItem} className="bg-blue-500 text-white px-4 py-2 rounded-r-md">Add</button>
                        </div>
                        <ul className="mt-2 text-sm list-disc list-inside">
                            {includedItems.map((item, index) => <li key={index}>{item}</li>)}
                        </ul>
                    </div>
                    {category === 'venues' && (
                        <div className="space-y-4">
                            <input type="number" placeholder="Capacity" value={capacity} onChange={e => setCapacity(e.target.value)} className="w-full p-2 border rounded-md" />
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Amenities</label>
                                <div className="flex">
                                    <input type="text" value={currentAmenity} onChange={e => setCurrentAmenity(e.target.value)} placeholder="e.g., Pool, Kitchen" className="flex-grow p-2 border rounded-l-md" />
                                    <button type="button" onClick={handleAddAmenity} className="bg-blue-500 text-white px-4 py-2 rounded-r-md">Add</button>
                                </div>
                                <ul className="mt-2 text-sm list-disc list-inside">
                                    {amenities.map((item, index) => <li key={index}>{item}</li>)}
                                </ul>
                            </div>
                            <textarea placeholder="House Rules" value={houseRules} onChange={e => setHouseRules(e.target.value)} className="w-full p-2 border rounded-md" rows="3"></textarea>
                        </div>
                    )}
                </div>

                {error && <p className="text-red-500">{error}</p>}
                <div className="flex justify-end space-x-2 mt-4">
                    <button type="button" onClick={onComplete} className="bg-gray-200 px-4 py-2 rounded-lg">Cancel</button>
                    <button type="submit" disabled={loading} className="bg-green-500 text-white px-6 py-2 rounded-lg">{loading ? 'Saving...' : 'Save Listing'}</button>
                </div>
            </form>
        </div>
    );
}